<template>
  <!-- Sidebar navigation component -->
  <nav id="docs-sidebar"
    class="hs-accordion-group py-2 hs-overlay hs-overlay-open:translate-x-0 -translate-x-full transition-all duration-300 transform hidden left-0 bottom-0 z-[60] bg-white border-r border-gray-200 pt-7 lg:block lg:translate-x-0 lg:right-auto lg:bottom-0 dark:scrollbar-y dark:bg-gray-800 dark:border-gray-700 border-l-2 border-gray-100 min-h-full flex flex-col justify-between">
    <!-- Conversation header -->
    <div class="mx-3 mb-4 mt-2">
      <div class="flex justify-between">
        <div>Conversation</div>
        <!-- Settings dropdown menu -->
        <div class="ml-3 relative">
          <jet-dropdown align="right">
            <template #trigger>
              <span class="inline-flex rounded-md">
                <button type="button"
                  class="inline-flex items-center text-sm leading-4 font-medium rounded-md text-gray-500 bg-white hover:text-gray-700 focus:outline-none transition">
                  <img :src="'/imgs/More.png'" alt="logo" class="pngs" />
                </button>
              </span>
            </template>

            <template #content>
              <form @submit.prevent="logout">
                <jet-dropdown-link href="/interface/settings">
                  Setting
                </jet-dropdown-link>
                <jet-dropdown-link as="button">
                  Support
                </jet-dropdown-link>
                <jet-dropdown-link as="button">
                  License
                </jet-dropdown-link>
                <jet-dropdown-link as="button" @click="openNewChat">
                  New Chat
                </jet-dropdown-link>
              </form>
            </template>
          </jet-dropdown>
        </div>
      </div>
    </div>

    <!-- Mobile number dropdown -->
    <div class="mx-3">
      <div class="relative">
        <select v-model="selectedMobile"
          class="mt-1 block w-full border border-gray-300 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-500 shadow-sm focus:outline-none focus:ring focus:border-blue-300">
          <option value="+911234567890">+91 9263637575</option>
          <option value="+911234567890">+91 1234567890</option>
          <option value="+910987654321">+91 0987654321</option>
          <option value="+917890654321">+91 7890654321</option>
        </select>
      </div>
    </div>

    <!-- Search box -->
    <div class="m-3">
      <div class="relative max-w-sm mx-auto">
        <input
          class="w-full h-9 py-2 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          type="search" placeholder="Search...">
      </div>
    </div>

    <!-- Conversation list accordion -->
    <div class="overflow-y-auto max-h-[calc(100vh-180px)]">
      <ul>
        <li v-for="(item, list) in unreadList" :key="list" @click="selectUnreadItem(item)"
          @mouseenter="hoveredItem = item" @mouseleave="hoveredItem = null"
          class="relative border-b border-gray-200 hover:bg-gray-100 cursor-pointer">
          <div class="flex p-3 pr-12 relative"> <!-- Added pr-12 for more options icon space -->
            <!-- Avatar with online status -->
            <div class="flex-shrink-0 relative">
              <div v-if="item.initials"
                class="rounded-full w-10 h-10 bg-gray-200 flex items-center justify-center text-gray-600 font-medium">
                {{ item.initials }}
              </div>
              <img v-else class="rounded-full w-10 h-10" src="https://randomuser.me/api/portraits/med/women/32.jpg"
                alt="User avatar" />
              <div class="absolute bottom-0 right-0 w-3 h-3 bg-green-400 border-2 border-white rounded-full"></div>
            </div>

            <!-- Message content -->
            <div class="ml-3 flex-1 min-w-0">
              <div class="flex justify-between items-center">
                <p class="text-sm font-medium text-gray-900">{{ item.name || item.participant_2_phone_no }}</p>
                <!-- <p class="text-xs text-gray-500">{{ item.timestamp || "recent" }}</p> -->
              </div>

              <!-- Message preview -->
              <div class="flex items-center">
                <i v-if="item.status" class="fa fa-check-double text-gray-400 mr-1 text-xs"></i>
                <i v-else class="fa fa-check text-gray-400 mr-1 text-xs"></i>
                <p v-if="item.type === 'text'"
                  :class="{ 'text-xs text-gray-500': item.status, 'text-xs font-medium text-gray-700': !item.status }"
                  class="truncate">
                  {{ item.message_content }}
                </p>
                <p v-else-if="item.type === 'image'"
                  :class="{ 'text-xs text-gray-500': item.status, 'text-xs font-medium text-gray-700': !item.status }"
                  class="truncate">
                  <i class="fa fa-image mr-1"></i> Image
                </p>
                <p v-else
                  :class="{ 'text-xs text-gray-500': item.status, 'text-xs font-medium text-gray-700': !item.status }"
                  class="truncate">
                  <i class="fa fa-file mr-1"></i> {{ item.document_name || "Document" }}
                </p>
              </div>
            </div>

            <!-- Unread count badge -->
            <div v-if="!item.status && item.unread_count"
              class="ml-2 bg-green-500 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
              {{ item.unread_count }}
            </div>

            <!-- Triple dot menu icon (visible on hover) -->
            <button v-if="hoveredItem === item" @click.stop="openOptionsMenu($event, item)"
              class="flex justify-between items-center ">
              <div class="flex flex-row items-center gap-1 transition-all duration-300"
                :class="{ 'transform -translate-y-1': optionsMenu.visible && optionsMenu.item === item }">
                <div class="w-1 h-1 rounded-full bg-gray-500"></div>
                <div class="w-1 h-1 rounded-full bg-gray-500"></div>
                <div class="w-1 h-1 rounded-full bg-gray-500"></div>
              </div>
            </button>
          </div>

          <!-- Options menu (appears when triple dot is clicked) -->
          <div v-if="optionsMenu.visible && optionsMenu.item === item"
            :style="{ top: `${optionsMenu.y}px`, right: `${optionsMenu.x}px` }"
            class="absolute z-10 bg-white shadow-lg rounded-md py-1 w-40">

            <button @click.stop="
              console.log('📤 Emitting view-profile:', item);
            $emit('view-profile', item)" class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
              View profile
            </button>
            <hr class="my-1 border-gray-200">
            <button @click.stop="deleteconversation(item)"
              class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
              Delete conversation
            </button>

            <button @click.stop="deletemessages(item)"
              class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
              Delete messages
            </button>

            <button @click.stop="pinChat(item)"
              class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
              Pin to top
            </button>
          </div>

        </li>
      </ul>
    </div>
  </nav>

  <!-- New Chat Modal -->
  <div v-if="showNewChatModal" class="fixed inset-0 z-10 flex items-center justify-center bg-black bg-opacity-50">
    <div class="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
      <div class="flex justify-between mb-4">
        <h2 class="text-lg font-semibold">Search Contacts</h2>
        <button @click="closeNewChatModal" class="text-gray-500 hover:text-gray-800">
          <i class="fa fa-times"></i>
        </button>
      </div>

      <!-- Search bar -->
      <div class="relative mb-4">
        <input v-model="searchQuery" type="text" placeholder="Search contacts..."
          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring focus:border-blue-500" />
      </div>

      <!-- New Chat option -->
      <ul class="max-h-60 overflow-y-auto">
        <li class="flex items-center justify-between p-2 hover:bg-gray-100 cursor-pointer" @click="openStartChatModal">
          New Chat
        </li>
      </ul>

      <!-- Contact list -->
      <ul class="max-h-40 overflow-y-auto" v-if="filteredContacts.length">
        <li v-for="contact in filteredContacts" :key="contact.id" @click="selectContact(contact)"
          class="flex items-center justify-between p-2 hover:bg-gray-100 cursor-pointer">
          <div>
            <p class="text-gray-700">{{ contact.name }}</p>
            <p class="text-sm text-gray-500">{{ contact.wa_phone_number }}</p>
          </div>
        </li>
      </ul>
      <div v-else-if="searchQuery && !contactFound">
        <p>No contacts found</p>
      </div>
    </div>
  </div>

  <!-- Start Chat Modal -->
  <div v-if="showStartChatModal" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div class="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
      <div class="flex justify-between mb-4">
        <h2 class="text-lg font-semibold">Start Chat</h2>
        <button @click="closeStartChatModal" class="text-gray-500 hover:text-gray-800">
          <i class="fa fa-times"></i>
        </button>
      </div>

      <!-- Textbox and Start Chat button -->
      <div class="mb-4">
        <input v-model="newChatText" type="text" placeholder="Enter your phone no."
          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring focus:border-blue-500" />
      </div>
      <button @click="startChat(newChatText)"
        class="w-full px-3 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600">
        Start Chat
      </button>
    </div>
  </div>
</template>


<script>
import AppLayout from "@/Layouts/AppLayout.vue";
import NavBar from "@/Layouts/NavBar.vue";
import JetResponsiveNavLink from "@/Jetstream/ResponsiveNavLink.vue";
import JetSideLink from "@/Jetstream/SideLink.vue";
import JetDropdown from "@/Jetstream/Dropdown.vue";
import JetDropdownLink from "@/Jetstream/DropdownLink.vue";
import { ref, watch } from 'vue';

export default {
  props: {
    selectedId: String,
    selectedType: String,
    item: {
      type: Object
    },

  },
  components: {
    AppLayout,
    NavBar,
    JetResponsiveNavLink,
    JetSideLink,
    JetDropdown,
    JetDropdownLink
  },
  data() {
    return {
      contextMenu: ref({
        visible: false,
        x: 0,
        y: -20,
        item: null,
      }),
      optionsMenu: {
        visible: false,
        x: 10,
        y: 0,
        item: null,
      },
      hoveredItem: null,
      unreadList: [],
      openContextMenu(event, item, index) {
        this.clickedElement = event.target.closest('li');  // Save clickedElement to this.clickedElement
        const { top: elementTop, left: elementLeft } = this.clickedElement.getBoundingClientRect();

        this.contextMenu = {
          visible: true,
          x: event.clientX - elementLeft,
          y: event.clientY - elementTop,
          item,
        };

        console.log(item);  // Now it should log the element as expected
      },

      contactList: [],
      showNewChatModal: ref(true),
      showStartChatModal: ref(false),
      unreadList: ref([]),
      showNewChatModal: false,
      selectedMobile: "+911234567890",
      showOptions: false,
      noOptions: false,
      accordions: {
        'users-accordion': true,
      },

      messagesList: [],
      searchQuery: '',
    };
  },
  computed: {
    filteredContacts() {
      return this.contactList.filter(contact =>
        contact.name.toLowerCase().includes(this.searchQuery.toLowerCase())
      );

    },
  },
  methods: {
    openOptionsMenu(event, item) {
      event.preventDefault();
      event.stopPropagation();

      this.optionsMenu = {
        visible: true,
        x: 10, // Distance from right
        y: 0,  // Position relative to the conversation item
        item,
      };

      // Close any other open menus
      this.contextMenu.visible = false;
    },
    searchContacts() {
      contactFound.value = false;
      filteredContacts.value = contacts.value.filter(contact =>
        contact.name.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
        contact.number.includes(searchQuery.value)
      )
      contactFound.value = filteredContacts.value.length > 0;
    },
    openStartChatModal() {
      this.showNewChatModal = false;
      this.showStartChatModal = true;
    },
    selectContact(contact) {
      this.$emit('contactSelected', contact);

      this.showNewChatModal = false;
    },

    closelist(item) {
      this.contextMenu.visible = false;
    },
    openNewChat() {
      this.showNewChatModal = true;
    },
    closeNewChatModal() {
      this.showNewChatModal = false;
      this.selectedContact = null;
    },

    closeStartChatModal() {
      this.showStartChatModal = false;
    },
    mobileOptions() {
      this.noOptions = !this.noOptions;
    },
    toggleOptions() {
      this.showOptions = !this.showOptions;

    },
    toggleSidebar() {
      this.isSidebarOpen = !this.isSidebarOpen;

    },
    toggleAccordion(accordionId) {
      this.accordions[accordionId] = !this.accordions[accordionId];

    },
    selectUnreadItem(item) {
      this.$emit('unread', item);
    },

    async deleteconversation(item) {
      try {
        const phoneNo = item.participant_2_phone_no;
        if (!phoneNo) {
          console.error('Participant phone number is missing.');
          alert("Participant phone number is missing.");
          return;
        }
        console.log('Making request with phone number:', phoneNo);

        await axios.post('/all/conversation/con-soft-delete', { participant_2_phone_no: phoneNo });
        this.contextMenu.visible = false;

        alert("Conversation soft deleted successfully.");
        this.fetchUnreadConversation();


      } catch (error) {
        console.error("Error soft deleting conversation:", error);
        alert("Failed to soft delete the conversation. Please try again.");
      }
    },

    async deletemessages(item) {
      try {
        const phoneNo = item.participant_2_phone_no;
        if (!phoneNo) {
          console.error('Participant phone number is missing.');
          alert("Participant phone number is missing.");
          return;
        }
        console.log('Making request with phone number:', phoneNo);

        await axios.post('/all/conversation/msg-soft-delete', { participant_2_phone_no: phoneNo });
        this.contextMenu.visible = false;

        alert("Messages soft deleted successfully.");
        this.fetchUnreadConversation();

      } catch (error) {
        console.error("Error soft deleting conversation:", error);
        alert("Failed to soft delete the conversation. Please try again.");
      }
    },


    startChat(phoneNumber) {
      if (!phoneNumber) {
        console.error('Phone number is required');
        return;
      }


      this.$emit('startchat', phoneNumber);


      this.showStartChatModal = false;




    },




    async fetchContacts() {
      axios.get('/all/conversation/fetchcontacts')
        .then(response => {
          this.contactList = response.data;
          console.log(this.contactList);

        })
        .catch(error => {
          console.error('Error fetching contacts:', error);
        });

    },
    async fetchUnreadConversation() {
      try {
        const response = await axios.get('/all/conversation/list'); // Adjust the endpoint as needed
        this.unreadList = response.data;
        console.log(this.unreadList); // Assuming the response data is an array of unread messages
      } catch (error) {
        console.error('Error fetching unread messages:', error);
      }
    },

  },

  mounted() {

    this.fetchUnreadConversation();
    this.fetchContacts();

    // js start
    // document.addEventListener('DOMContentLoaded', function () {
    // Users Dropdown
    // const usersAccordionToggle = document.querySelector('#users-accordion span.hs-accordion-toggle');
    // const usersAccordionContent = document.querySelector('#users-accordion .hs-accordion-content');

    // usersAccordionToggle.addEventListener('click', function () {
    //   usersAccordionContent.classList.toggle('hidden');
    // });

    // Account Dropdown
    const accountAccordionToggle = document.querySelector('#account-accordion span.hs-accordion-toggle');
    const accountAccordionContent = document.querySelector('#account-accordion .hs-accordion-content');

    // accountAccordionToggle.addEventListener('click', function () {
    //   // Toggle the 'hidden' class on the content to show/hide it
    //   accountAccordionContent.classList.toggle('hidden');
    //   });
    // });
    // js end
    // }
    document.addEventListener('click', (event) => {
      if (this.optionsMenu.visible) {
        this.optionsMenu.visible = false;
      }
      if (this.contextMenu.visible && !this.clickedElement.contains(event.target)) {
        this.contextMenu.visible = false;
      }
    });


  },



}

</script>

<style>
.pad {
  padding: 0 20px;
}

.logo {
  width: 50px;
}

p {
  margin-bottom: 0.8rem;
}

.text-xs {
  font-size: 0.75rem;
  line-height: 1rem;
}

.text-sm {
  font-size: 0.875rem;
  line-height: 1rem;
}

.position-sticky {
  position: sticky;
  top: 0;
  /* Adjust this value based on your design */
  z-index: 100;
  /* Adjust this value based on your design */
  background-color: #fff;
  /* Adjust this value based on your design */
}

/* .pad:hover{
  border-left: 4px solid rgb(192, 191, 191);
} */
.pb-2 {
  padding-bottom: .01rem !important;
}

.button {
  color: #007bff;
  background: none;
  border: none;
  cursor: pointer;
  text-decoration: underline;
}

.truncate {
  max-width: 220px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.custom-context-menu {
  width: 150px;
  /* Adjust this value to make the menu narrower */
}

.custom-context-menu li {
  padding: 8px 12px;
}

.conversation-list {
  max-height: 28rem;
  /* Keeps the current height limit */
  overflow-y: auto;
  /* Enable scrolling when needed */
}

/* Hide scrollbar when not needed */
.conversation-list.few-items {
  overflow-y: visible;
  height: auto;
}

/* Optional styling for scrollbar */
.scrollbar-y {
  scrollbar-width: thin;
  scrollbar-color: rgba(156, 163, 175, 0.5) transparent;
}

.scrollbar-y::-webkit-scrollbar {
  width: 6px;
}

.scrollbar-y::-webkit-scrollbar-track {
  background: transparent;
}

.scrollbar-y::-webkit-scrollbar-thumb {
  background-color: rgba(156, 163, 175, 0.5);
  border-radius: 20px;
}
</style>